// src/components/Layout.tsx — top bar + nav shell. Nav links are role-aware.
import type { ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/auth/AuthProvider";

interface NavItem {
  href: string;
  label: string;
}

const USER_NAV: NavItem[] = [
  { href: "/", label: "Home" },
  { href: "/body", label: "My Body" },
  { href: "/training", label: "My Training" },
  { href: "/peptides", label: "My Peptides" },
  { href: "/conversations", label: "My Conversations" },
];

const ADMIN_NAV: NavItem[] = [
  { href: "/", label: "Admin Home" },
  { href: "/admin/participants", label: "Participants" },
  { href: "/admin/audit-log", label: "Audit Log" },
];

export function Layout({ children }: { children: ReactNode }) {
  const { email, isAdmin, signOut } = useAuth();
  const [location] = useLocation();
  const nav = isAdmin ? ADMIN_NAV : USER_NAV;

  return (
    <div className="min-h-full">
      <header className="sticky top-0 z-10 border-b border-white/10 bg-ls-bg/80 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-6">
            <Link href="/" className="font-mono text-lg font-bold text-ls-fg">
              PULSE
            </Link>
            <nav className="hidden items-center gap-1 sm:flex">
              {nav.map((item) => {
                const active =
                  item.href === "/" ? location === "/" : location.startsWith(item.href);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`rounded-md px-3 py-1.5 text-sm transition ${
                      active
                        ? "bg-white/10 text-ls-fg"
                        : "text-ls-muted hover:text-ls-fg"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin ? (
              <span className="rounded-full bg-ls-accent/20 px-2 py-0.5 text-xs font-medium text-ls-accent">
                admin
              </span>
            ) : null}
            <span className="hidden text-xs text-ls-muted sm:inline">{email}</span>
            <button
              onClick={() => void signOut()}
              className="text-xs text-ls-muted underline-offset-2 hover:text-ls-fg hover:underline"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-8">{children}</main>
    </div>
  );
}
